import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookie-based admin sessions: `withCredentials: true` sends the HttpOnly
// session cookie back on every request. The `X-Requested-With: fetch` header
// is our CSRF guard — browsers block cross-origin requests carrying custom
// headers unless CORS explicitly allows them (server.py restricts CORS to the
// site's own origin), so state-changing calls can only originate from our UI.
const client = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "X-Requested-With": "fetch" },
});

const PUBLIC_SPEC_PLACEHOLDERS = new Set([
  "",
  "-",
  "—",
  "n/a",
  "na",
  "not available",
  "unknown",
  "none",
  "null",
  "needs confirmation",
  "to be confirmed",
  "to be confirmed before order",
]);

// Known internal classification values that were written into legacy product
// spec fields for editorial/cataloguing purposes. These are not customer-facing
// specifications and should not propagate into product or project pages.
const PUBLIC_INTERNAL_SPEC_VALUES = new Set([
  "heritage / classical indian luxury",
]);

const sanitizePublicSpecs = (specs) => {
  if (!specs || typeof specs !== "object" || Array.isArray(specs)) return specs;
  return Object.fromEntries(
    Object.entries(specs).filter(([, value]) => {
      if (value == null) return false;
      const normalized = String(value).trim().toLowerCase();
      return normalized
        && !PUBLIC_SPEC_PLACEHOLDERS.has(normalized)
        && !PUBLIC_INTERNAL_SPEC_VALUES.has(normalized);
    })
  );
};

// Some legacy descriptions contain a final blank-line-separated SEO keyword
// bank (for example: "elephant arm chandelier gajmahal chandelier ...").
// Keep that stored Admin copy intact, but remove the unmistakable keyword-only
// tail from public responses so customers see editorial description only.
const sanitizePublicDescription = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  const blocks = trimmed.split(/\n\s*\n/).filter(Boolean);
  if (blocks.length < 2) return trimmed;

  const tail = blocks[blocks.length - 1].trim();
  const words = tail.split(/\s+/).filter(Boolean);
  const lightingTerm = /\b(chandelier|light|lighting|lamp|glass|crystal|lantern|sconce|ceiling|wall|floor|table|hanging)\b/i;
  const looksLikeKeywordBank =
    tail.length >= 60 &&
    tail.length <= 600 &&
    words.length >= 8 &&
    tail === tail.toLowerCase() &&
    !/[.!?•:]/.test(tail) &&
    lightingTerm.test(tail);

  return looksLikeKeywordBank ? blocks.slice(0, -1).join("\n\n").trim() : trimmed;
};

// Product tags are catalogue/search metadata. They include internal collection
// tokens and SEO keyword phrases that must remain available to Admin/back-end
// workflows but must never be rendered as customer-facing product copy.
// Editorial spec placeholders and known internal taxonomy values are handled
// the same way: Admin keeps the raw values, while public views receive only
// confirmed customer-facing specifications.
export const sanitizePublicProduct = (product) => {
  if (!product || typeof product !== "object") return product;
  return {
    ...product,
    short_description: sanitizePublicDescription(product.short_description),
    description: sanitizePublicDescription(product.description),
    tags: [],
    specs: sanitizePublicSpecs(product.specs),
  };
};

export const prepareListedProduct = (product, { raw = false } = {}) =>
  raw ? product : sanitizePublicProduct(product);

export const api = {
  searchByImage: async (file, signal) => {
    const form = new FormData();
    form.append("file", file);
    return (await client.post("/search/image", form, { signal, timeout: 25000 })).data;
  },
  getImageSearchJob: (jobId, signal) => client.get(
    `/search/image/jobs/${encodeURIComponent(jobId)}`,
    { signal, timeout: 10000 },
  ).then(r => r.data),
  adminImageSearchReferences: () => client.get(
    "/admin/customer-image-search/references",
  ).then(r => r.data),
  adminAddImageSearchReference: (file, productIds, relationships = {}) => {
    const form = new FormData();
    form.append("file", file);
    form.append("product_ids", JSON.stringify(productIds.map((id) => ({
      id,
      relationship: relationships[id] || "exact",
    }))));
    return client.post("/admin/customer-image-search/references", form, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 45000,
    }).then(r => r.data);
  },
  adminDeleteImageSearchReference: (id) => client.delete(
    `/admin/customer-image-search/references/${encodeURIComponent(id)}`,
  ).then(r => r.data),
  authMe: () => client.get("/auth/me").then(r => r.data),
  authSession: (session_id) => client.post("/auth/session", { session_id }).then(r => r.data),
  authLogout: () => client.post("/auth/logout").then(r => r.data),
  listProducts: (params = {}) => client.get("/products", { params }).then(r => r.data),
  /**
   * Fetch every page of /products so callers that need the full catalogue
   * (Admin, catalogue PDF, gallery cross-references, styled-by page, etc.)
   * can still get a flat array of items. The public cap is 48/page, admins
   * get 5000/page — either way we page until total_pages is reached.
   *
   * This helper is consumed by public catalogue/project presentation code, so
   * each returned product is passed through the same public sanitizer used by
   * getProduct(). Raw Admin exports remain available through adminProductsExport.
   */
  adminProductsExport: () => client.get("/admin/products/export").then(r => r.data),

  // --- Admin deletion helpers (Batch B) ---
  adminDeleteInquiry: (id) => client.delete(`/inquiries/${encodeURIComponent(id)}`).then(r => r.data),
  adminGalleryCleanupOrphans: () => client.post("/admin/gallery/cleanup-orphans", {}).then(r => r.data),
  adminBulkDeleteInquiries: (ids) => client.post("/admin/inquiries/bulk-delete", { ids }).then(r => r.data),
  adminDeleteContactMessage: (id) => client.delete(`/contact-messages/${encodeURIComponent(id)}`).then(r => r.data),
  adminBulkDeleteContactMessages: (ids) => client.post("/admin/contact-messages/bulk-delete", { ids }).then(r => r.data),

  listAllProducts: async (params = {}) => {
    const collected = [];
    const seen = new Set();
    let page = 1;
    // Admin editors need the stored SOP fields exactly as persisted, including
    // explicit confirmation fallbacks. Public consumers still receive the
    // sanitized specification set by default.
    const { raw = false, ...requestParams } = params;
    const per = requestParams.limit || 48;
    // Hard safety cap so a runaway loop can never happen.
    for (let i = 0; i < 200; i++) {
      const res = await client
        .get("/products", { params: { ...requestParams, page, limit: per } })
        .then((r) => r.data);
      const items = res?.items || [];
      for (const p of items) {
        if (p?.id && !seen.has(p.id)) {
          seen.add(p.id);
          collected.push(prepareListedProduct(p, { raw }));
        }
      }
      const totalPages = res?.total_pages || 1;
      if (page >= totalPages || items.length === 0) break;
      page += 1;
    }
    return collected;
  },
  getProduct: (id) => client.get(`/products/${id}`).then(r => sanitizePublicProduct(r.data)),
  getProductVariants: (id) => client.get(`/product-variants/${encodeURIComponent(id)}`).then(r => ({
    ...r.data,
    items: (r.data?.items || []).map(sanitizePublicProduct),
  })),
  getVariantFamilyIndex: () => client.get("/variant-family-index").then(r => r.data?.items || {}),
  createProduct: (data) => client.post("/products", data).then(r => r.data),
  updateProduct: (id, data, { reason = "", source = "admin" } = {}) => client.put(
    `/products/${id}`,
    data,
    { params: { change_reason: reason || "Product edited in Admin", change_source: source } },
  ).then(r => r.data),
  deleteProduct: (id) => client.delete(`/products/${id}`).then(r => r.data),
  listProductVersions: (id, limit = 50) => client.get(
    `/admin/products/${id}/versions`, { params: { limit } },
  ).then(r => r.data),
  restoreProductVersion: (id, versionId, reason = "") => client.post(
    `/admin/products/${id}/versions/${versionId}/restore`, { reason },
  ).then(r => r.data),
  previewBulkProductUpdate: (ids, changes) => client.post(
    "/admin/products/bulk/preview", { ids, changes },
  ).then(r => r.data),
  applyBulkProductUpdate: (ids, changes, previewToken, reason = "") => client.post(
    "/admin/products/bulk/apply",
    { ids, changes, preview_token: previewToken, reason },
  ).then(r => r.data),
  categories: () => client.get("/products/categories").then(r => r.data),

  listReviews: (product_id) => client.get(`/reviews`, { params: { product_id } }).then(r => r.data),
  createReview: (data) => client.post(`/reviews`, data).then(r => r.data),

  // Admin-only review moderation
  adminListReviews: (params = {}) => client.get("/admin/reviews", { params }).then(r => r.data),
  adminReviewCounts: () => client.get("/admin/reviews/counts").then(r => r.data),
  adminApproveReview: (id) => client.post(`/admin/reviews/${id}/approve`).then(r => r.data),
  adminRejectReview: (id) => client.post(`/admin/reviews/${id}/reject`).then(r => r.data),
  adminDeleteReview: (id) => client.delete(`/admin/reviews/${id}`).then(r => r.data),

  createInquiry: (data) => client.post("/inquiries", data).then(r => r.data),
  listInquiries: () => client.get("/inquiries").then(r => r.data),
  updateInquiryStatus: (id, status) => client.patch(`/inquiries/${id}`, null, { params: { status } }).then(r => r.data),
  listInquiryQuotations: (id) => client.get(
    `/admin/inquiries/${encodeURIComponent(id)}/quotations`,
  ).then(r => r.data),
  listStandaloneQuotations: () => client.get("/admin/quotations").then(r => r.data),
  updateQuotation: (id, data) => client.put(`/admin/quotations/${encodeURIComponent(id)}`, data).then(r => r.data),
  deleteQuotation: (id) => client.delete(`/admin/quotations/${encodeURIComponent(id)}`).then(r => r.data),
  createStandaloneQuotation: (data) => client.post("/admin/quotations", data).then(r => r.data),
  createInquiryQuotation: (id, data) => client.post(
    `/admin/inquiries/${encodeURIComponent(id)}/quotations`, data,
  ).then(r => r.data),
  aiQuotationCustomisation: (data) => client.post(
    "/ai/quotation-customisation", data,
  ).then(r => r.data),
  matchQuotationProductByImage: (file, limit = 5, quick = false) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(
      "/admin/quotations/product-match-by-image",
      fd,
      {
        params: { limit, quick },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: quick ? 45000 : 55000,
      },
    ).then(r => r.data);
  },
  startQuotationImageDetailJob: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/admin/quotations/product-match-by-image-jobs", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 45000,
    }).then(r => r.data);
  },
  getQuotationImageDetailJob: (jobId) => client.get(
    `/admin/quotations/product-match-by-image-jobs/${encodeURIComponent(jobId)}`,
  ).then(r => r.data),
  diagnoseQuotationProductByImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(
      "/admin/quotations/product-match-diagnostics",
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    ).then(r => r.data);
  },

  createContact: (data) => client.post("/contact", data).then(r => r.data),
  listContact: () => client.get("/contact").then(r => r.data),

  getSettings: () => client.get("/settings").then(r => r.data),
  getCollectionsIndex: () => client.get("/collections-index").then(r => r.data?.items || []),
  getCollection: (slug) => client.get(`/collections/${encodeURIComponent(slug)}`).then(r => ({
    ...r.data,
    items: (r.data?.items || []).map(sanitizePublicProduct),
  })),
  // Admin-only: returns the full Settings model including secrets like
  // google_maps_api_key. Public `/settings` deliberately omits those.
  adminGetSettings: () => client.get("/admin/settings").then(r => r.data),
  updateSettings: (data) => client.put("/settings", data).then(r => r.data),
  adminImageProtectionStatus: () => client.get("/image-protection/status").then(r => r.data),
  adminImageOwnershipRegistry: (params = {}) => client.get("/image-protection/registry", { params }).then(r => r.data),
  adminProtectExistingImages: (limit = 10) => client.post("/image-protection/reprocess", {}, { params: { limit } }).then(r => r.data),
  adminRepairFailedImages: (limit = 25) => client.post("/image-protection/repair-failed", {}, { params: { limit } }).then(r => r.data),
  adminReprocessVisibleWatermarks: () => client.post("/watermark/reprocess", {}).then(r => r.data),
  adminUploadQuotationBrandAsset: (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client.post(
      `/admin/quotation-branding/${encodeURIComponent(kind)}`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    ).then(r => r.data);
  },
  adminClearQuotationBrandAsset: (kind) => client.delete(
    `/admin/quotation-branding/${encodeURIComponent(kind)}`,
  ).then(r => r.data),

  // --- Hero slider ---
  getHeroSlideshow: () => client.get("/hero-slides").then(r => r.data),
  adminListHeroSlides: () => client.get("/admin/hero-slides").then(r => r.data),
  adminGetHeroSettings: () => client.get("/admin/hero-settings").then(r => r.data),
  adminUpdateHeroSettings: (data) => client.patch("/admin/hero-settings", data).then(r => r.data),
  adminUploadHeroSlide: (file, altText = "") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", altText || "");
    return client.post("/admin/hero-slides", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  adminUpdateHeroSlide: (id, data) => client.patch(`/admin/hero-slides/${id}`, data).then(r => r.data),
  adminReorderHeroSlides: (orderIds) => client.patch(`/admin/hero-slides/reorder`, { order: orderIds }).then(r => r.data),
  adminDeleteHeroSlide: (id) => client.delete(`/admin/hero-slides/${id}`).then(r => r.data),

  // --- Category featured images ---
  // Public map of `category -> image_url` (only categories with an admin override).
  getCategoryFeaturedImages: () => client.get("/category-featured-images").then(r => r.data),
  // Admin CRUD
  adminListCategoryFeatured: () => client.get("/admin/category-featured-images").then(r => r.data),
  adminSetCategoryFeaturedFromProduct: (category, product_id, image_url) =>
    client
      .put(`/admin/category-featured-images/${encodeURIComponent(category)}`, { product_id, image_url })
      .then(r => r.data),
  adminUploadCategoryFeatured: (category, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post(`/admin/category-featured-images/${encodeURIComponent(category)}/upload`, fd,
        { headers: { "Content-Type": "multipart/form-data" } })
      .then(r => r.data);
  },
  adminResetCategoryFeatured: (category) =>
    client.delete(`/admin/category-featured-images/${encodeURIComponent(category)}`).then(r => r.data),

  // --- Central media library ---
  adminMediaLibrary: () => client.get("/admin/media-library").then(r => r.data),
  adminUpdateMediaAsset: (assetId, data) =>
    client.patch(`/admin/media-library/${assetId}`, data).then(r => r.data),
  adminApplyMediaRecommendations: (items) =>
    client.post("/admin/media-library/recommendations/apply", { items }).then(r => r.data),
  adminScanMediaLibrary: (limit = 250) =>
    client.post(`/admin/media-library/scan?limit=${limit}`).then(r => r.data),
  adminMediaOriginalUrl: (fileId) =>
    `${API}/admin/media-library/original/${encodeURIComponent(fileId)}`,

  upload: (file) => {
    // Client-side guard so users get a friendly message before the network round-trip.
    // Server hard cap: 25MB for images, 100MB for videos.
    const isVideo = (file?.type || "").toLowerCase().startsWith("video/");
    const MAX = isVideo ? 100 * 1024 * 1024 : 25 * 1024 * 1024;
    if (file && file.size > MAX) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      if (isVideo) {
        return Promise.reject(new Error(`Video is ${sizeMB}MB — max 100MB. Trim/compress the clip before uploading.`));
      }
      return Promise.reject(new Error(`Image is ${sizeMB}MB — max 25MB. Please compress with tinypng.com or resize to ~2000px on the long edge.`));
    }
    const fd = new FormData();
    fd.append("file", file);
    return client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },

  exportCsvUrl: () => `${API}/export/products.csv`,
  stats: () => client.get("/stats").then(r => r.data),
  googleReviews: () => client.get("/google/reviews").then(r => r.data),
  pullInstagramCover: (url) => client.post("/admin/instagram/cover", { url }).then(r => r.data),
  requestCatalogue: (name, phone, source = "contact_page") =>
    client.post("/catalogue-request", { name, phone, source }).then(r => r.data),
  aiGenerateProduct: (image_url) =>
    client.post("/ai/generate-product", { image_url }).then(r => r.data),
  aiGenerateProductsBulk: (images) =>
    client.post("/ai/generate-products-bulk", { images }).then(r => r.data),
  aiAnalyzeProductBatch: (items) =>
    client.post("/ai/analyze-product-batch", { items }).then(r => r.data),
  aiCommitProductBatch: (products) =>
    client.post("/ai/commit-product-batch", { products }).then(r => r.data),
  aiReviseProductConversation: (payload) =>
    client.post("/ai/revise-product-conversation", payload).then(r => r.data),
  adminProductSop: () => client.get("/admin/product-sop").then(r => r.data),
  aiNameSuggestions: (opts) =>
    client.post("/ai/name-suggestions", opts).then(r => r.data),
  aiRegenerateDetails: (opts) =>
    client.post("/ai/regenerate-details", opts).then(r => r.data),
  aiRegenerateFromName: (opts) =>
    client.post("/ai/regenerate-from-name", opts).then(r => r.data),
  resolveImage: (u, opts = {}) => {
    if (!u) return "";
    if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
    if (u.startsWith("http")) {
      if (opts.proxy) return `${API}/proxy-image?url=${encodeURIComponent(u)}`;
      return u;
    }
    return u;
  },
};

export const formatPrice = (v, symbol = "₹") => {
  if (v == null) return "";
  return `${symbol}${Number(v).toLocaleString("en-IN")}`;
};

// Formats an Indian mobile like "+91 89203 92937" for premium display.
// Falls back gracefully for non-Indian or malformed numbers.
export const formatPhone = (raw) => {
  if (!raw) return "";
  const digits = String(raw).replace(/[^0-9]/g, "");
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return raw;
};

// Luxury price rendering — single source of truth for cards, product detail,
// wishlist, catalogue and gallery lists. Returns a structured object so views
// can style each part however they want.
//
//   product.price_display: "starting_from" (default) | "fixed" | "on_request"
//   product.fixed_price: legacy boolean — mapped to "fixed" when set.
//   product.compare_at_price: only shown if > 0 AND > product.price.
export const formatProductPrice = (product, symbol = "₹") => {
  if (!product) return { onRequest: false, label: null, primary: "", compareAt: null };

  // Resolve display mode with legacy fallback
  let mode = product.price_display;
  if (!mode) mode = product.fixed_price ? "fixed" : "starting_from";

  // Fallback: if the price is missing or 0 (and not explicitly free), always
  // show "Price on request" instead of a bare "₹0" or blank field.
  const numericPrice = Number(product.price);
  if (!Number.isFinite(numericPrice) || numericPrice <= 0) {
    return { onRequest: true, label: null, primary: "Price on request", compareAt: null };
  }

  if (mode === "on_request") {
    return { onRequest: true, label: null, primary: "Price on request", compareAt: null };
  }

  const primary = product.price != null ? formatPrice(product.price, symbol) : "";
  const label = mode === "fixed" ? null : "From";

  const cmp = Number(product.compare_at_price);
  const showCompare =
    Number.isFinite(cmp) && cmp > 0 && Number(product.price) > 0 && cmp > Number(product.price);
  const compareAt = showCompare ? formatPrice(cmp, symbol) : null;

  return { onRequest: false, label, primary, compareAt };
};

// Legacy plain-text helper kept for print/PDF views.
export const formatLuxuryPrice = (product, symbol = "₹") => {
  const p = formatProductPrice(product, symbol);
  if (p.onRequest) return "Price on request";
  return p.label ? `${p.label} ${p.primary}` : p.primary;
};

// --- SKU sorting ------------------------------------------------------------
// Natural sort keyed on the trailing numeric segment of the SKU. Splits on
// dashes/underscores so `SGE-CH-001` → prefix `SGE-CH`, num 1. Non-numeric
// tails (e.g. `SGE-HL-DMC`) get num = +Infinity so they always sort AFTER
// numbered SKUs within the same prefix, per the owner's spec.
export function skuSortKey(sku) {
  if (!sku) return { prefix: "\uFFFF", num: Number.POSITIVE_INFINITY, tail: "" };
  const parts = String(sku).split(/[-_]/);
  const last = parts.length > 1 ? parts[parts.length - 1] : "";
  const prefix = parts.slice(0, Math.max(1, parts.length - 1)).join("-").toUpperCase();
  const num = /^\d+$/.test(last) ? parseInt(last, 10) : Number.POSITIVE_INFINITY;
  return { prefix, num, tail: last.toUpperCase() };
}

export function compareBySku(a, b, dir = "asc") {
  const A = skuSortKey(a?.sku);
  const B = skuSortKey(b?.sku);
  const mult = dir === "desc" ? -1 : 1;
  if (A.prefix !== B.prefix) return mult * A.prefix.localeCompare(B.prefix);
  if (A.num !== B.num) return mult * (A.num - B.num);
  return mult * A.tail.localeCompare(B.tail);
}

export default api;
